<template>
    <v-container
        class="container"
        v-if="isLoading == false">
        <div class="tw-flex tw-flex-col lg:tw-justify-between lg:tw-flex-row">
            <h1 class="tw-font-bold tw-text-secondary tw-text-center lg:tw-text-left tw-text-3xl tw-underline tw-underline-offset-1 tw-decoration-primary tw-decoration-4">
                {{ fund[0].name }}
            </h1>
        </div>
        <!-- Breadcrumbs -->
        <div class="tw-text-lg tw-text-secondary tw-text-left tw-hidden lg:tw-block">
            <v-breadcrumbs
                :items="breadCrumbItems"
                active-class="!tw-font-semibold"
                class="!tw-pl-0">
                <template v-slot:divider>
                    <v-icon icon="mdi-chevron-right"></v-icon>
                </template>
            </v-breadcrumbs>
        </div>
        <div class="tw-bg-white tw-rounded-lg tw-mt-5 lg:tw-mt-1">
            <!-- Investments by Categories -->
            <v-expansion-panels
                class="!tw-p-1 !tw-z-auto !tw-rounded-lg"
                v-model="panel">
                <v-expansion-panel
                    v-for="f in fund"
                    :key="f"
                    class="tw-gap-y-4">
                    <v-expansion-panel-title
                        class="no-opacity !tw-px-3"
                        v-if="f.investments !== null">
                        <template v-slot:default="{ expanded }">
                            <div>
                                <!-- <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-xl tw-ml-[-1.625rem]">{{ investment.category }}</p> -->
                                <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-xl">{{ $t('reservedArea.investmentDetails.category') + f.investments[0].category }}</p>
                            </div>
                            <v-fade-transition leave-absolute>
                                <span v-if="expanded"></span>
                            </v-fade-transition>
                        </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text v-if="f.investments !== null">
                        <v-row>
                            <v-col
                                cols="12"
                                md="6"
                                class="!tw-px-1">
                                <v-card
                                    class="tw-mx-auto"
                                    variant="flat"
                                    rounded="lg">
                                    <template v-slot:title>
                                        <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                            {{ $t('reservedArea.investmentDetails.upValue') }}
                                        </p>
                                    </template>
                                    <template v-slot:text>
                                        <p class="tw-text-secondary tw-text-left tw-text-base">
                                            {{ f.priceDate ? formatDate(f.priceDate) : 'N/A' }}
                                        </p>
                                    </template>
                                    <v-card-actions>
                                        <v-list-item class="w-100">
                                            <v-list-item-title>
                                                <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                                    {{ getUpValue(f.investments) }}
                                                </p>
                                            </v-list-item-title>
                                            <template v-slot:append>
                                                <div class="tw-flex tw-items-center tw-justify-center tw-bg-gray-default tw-rounded-full tw-h-12 tw-w-12 tw-mt-2">
                                                    <v-list-item-icon>
                                                        <v-icon>
                                                            <v-img src="/images/ReservedArea/icons/chart.svg"></v-img>
                                                        </v-icon>
                                                    </v-list-item-icon>
                                                </div>
                                            </template>
                                        </v-list-item>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                            <v-col
                                cols="12"
                                md="6"
                                v-if="isSelectOrShortTermFund(f.name)"
                                class="!tw-px-1">
                                <v-card
                                    class="tw-mx-auto"
                                    variant="flat"
                                    rounded="lg">
                                    <template v-slot:title>
                                        <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                            {{ $t('reservedArea.investments.participationUnits') }}
                                        </p>
                                    </template>
                                    <template v-slot:text>
                                        <p class="tw-text-secondary tw-text-left tw-text-base">&nbsp;</p>
                                    </template>
                                    <v-card-actions>
                                        <v-list-item class="w-100">
                                            <v-list-item-title>
                                                <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                                    {{ getParticipationUnits(f.investments) }} {{ $t('reservedArea.investments.units') }}
                                                </p>
                                            </v-list-item-title>
                                            <template v-slot:append>
                                                <div class="tw-flex tw-items-center tw-justify-center tw-bg-gray-default tw-rounded-full tw-h-12 tw-w-12 tw-mt-2">
                                                    <v-list-item-icon>
                                                        <v-icon>
                                                            <v-img src="/images/ReservedArea/icons/euro.svg"></v-img>
                                                        </v-icon>
                                                    </v-list-item-icon>
                                                </div>
                                            </template>
                                        </v-list-item>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                            <v-col
                                cols="12"
                                md="6"
                                v-if="!isSelectOrShortTermFund(f.name)"
                                class="!tw-px-1">
                                <v-card
                                    class="tw-mx-auto"
                                    variant="flat"
                                    rounded="lg">
                                    <template v-slot:title>
                                        <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                            {{ $t('reservedArea.investments.acquisitionValueInitial') }}
                                        </p>
                                    </template>
                                    <template v-slot:text>
                                        <p class="tw-text-secondary tw-text-left tw-text-base">&nbsp;</p>
                                    </template>
                                    <v-card-actions>
                                        <v-list-item class="w-100">
                                            <v-list-item-title>
                                                <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                                    {{ getAcquisitionValueInitial(f.investments) }}
                                                </p>
                                            </v-list-item-title>
                                            <template v-slot:append>
                                                <div class="tw-flex tw-items-center tw-justify-center tw-bg-gray-default tw-rounded-full tw-h-12 tw-w-12 tw-mt-2">
                                                    <v-list-item-icon>
                                                        <v-icon>
                                                            <v-img src="/images/ReservedArea/icons/wallet.svg"></v-img>
                                                        </v-icon>
                                                    </v-list-item-icon>
                                                </div>
                                            </template>
                                        </v-list-item>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                            <v-col
                                cols="12"
                                md="6"
                                class="!tw-px-1">
                                <v-card
                                    class="tw-mx-auto"
                                    variant="flat"
                                    rounded="lg">
                                    <template v-slot:title>
                                        <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                            {{
                                                isSelectOrShortTermFund(f.name)
                                                    ? $t('reservedArea.investments.acquisitionValue')
                                                    : $t('reservedArea.investments.acquisitionValueCurrent')
                                            }}
                                        </p>
                                    </template>
                                    <template v-slot:text>
                                        <p class="tw-text-secondary tw-text-left tw-text-base">&nbsp;</p>
                                    </template>
                                    <v-card-actions>
                                        <v-list-item class="w-100">
                                            <v-list-item-title>
                                                <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                                    {{ getAcquisitionValueCurrent(f.investments, f.name) }}
                                                </p>
                                            </v-list-item-title>
                                            <template v-slot:append>
                                                <div class="tw-flex tw-items-center tw-justify-center tw-bg-gray-default tw-rounded-full tw-h-12 tw-w-12 tw-mt-2">
                                                    <v-list-item-icon>
                                                        <v-icon>
                                                            <v-img src="/images/ReservedArea/icons/wallet.svg"></v-img>
                                                        </v-icon>
                                                    </v-list-item-icon>
                                                </div>
                                            </template>
                                        </v-list-item>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                            <v-col
                                cols="12"
                                md="6"
                                class="!tw-px-1"
                                v-for="(i, index) in visibleCardIndices"
                                :key="i + 3">
                                <v-card
                                    class="tw-mx-auto"
                                    variant="flat"
                                    rounded="lg">
                                    <template v-slot:title>
                                        <p
                                            v-if="i == 1"
                                            class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                            {{ $t('reservedArea.investments.currentValuation') }}
                                        </p>
                                        <p
                                            v-if="i == 2"
                                            class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                            {{ $t('reservedArea.investments.participationUnits') }}
                                        </p>
                                        <p
                                            v-if="i == 3"
                                            class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                            {{ $t('reservedArea.investments.returnedCapital') }}
                                        </p>
                                        <p
                                            v-if="i == 4"
                                            class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                            {{ $t('reservedArea.investments.distributedDividends') }}
                                        </p>
                                    </template>
                                    <template v-slot:text>
                                        <p
                                            v-if="i == 1"
                                            class="tw-text-secondary tw-text-left tw-text-base">
                                            {{ f.priceDate ? formatDate(f.priceDate) : 'N/A' }}
                                        </p>
                                        <p
                                            v-if="i == 2 || i == 3 || i == 4"
                                            class="tw-text-secondary tw-text-left tw-text-base">
                                            &nbsp;
                                        </p>
                                    </template>
                                    <v-card-actions>
                                        <v-list-item class="w-100">
                                            <v-list-item-title v-if="i == 1">
                                                <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                                    {{ getCurrentValuation(f.investments) }}
                                                </p>
                                            </v-list-item-title>
                                            <v-list-item-title v-if="i == 2">
                                                <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                                    {{ getParticipationUnits(f.investments) }} {{ $t('reservedArea.investments.units') }}
                                                </p>
                                            </v-list-item-title>
                                            <v-list-item-title v-if="i == 3">
                                                <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                                    {{ getReturnedCapital(f.investments, f.name) }}
                                                </p>
                                            </v-list-item-title>
                                            <v-list-item-title v-if="i == 4">
                                                <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-lg">
                                                    {{ getDistributedDividends(f.investments, f.name) }}
                                                </p>
                                            </v-list-item-title>
                                            <template v-slot:append>
                                                <div class="tw-flex tw-items-center tw-justify-center tw-bg-gray-default tw-rounded-full tw-h-12 tw-w-12 tw-mt-2">
                                                    <v-list-item-icon>
                                                        <v-icon>
                                                            <v-img
                                                                v-if="i == 1"
                                                                src="/images/ReservedArea/icons/graph.svg"></v-img>
                                                            <v-img
                                                                v-if="i == 2 || i == 3 || i == 4"
                                                                src="/images/ReservedArea/icons/euro.svg"></v-img>
                                                        </v-icon>
                                                    </v-list-item-icon>
                                                </div>
                                            </template>
                                        </v-list-item>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-expansion-panel-text>
                </v-expansion-panel>
            </v-expansion-panels>
            <!-- Description -->
            <v-row class="tw-justify-center tw-gap-x-4 tw-p-4">
                <v-col
                    cols="12"
                    class="tw-bg-gray-lighter-lighter tw-rounded-lg tw-mt-[-0.23rem]">
                    <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-xl tw-mb-2">{{ $t('reservedArea.investmentDetails.description') }}</p>
                    <p class="tw-text-secondary tw-text-left tw-text-lg">
                        {{ fund[0].description ? fund[0].description : 'N/A' }}
                    </p>
                </v-col>
            </v-row>
            <!-- Documents -->
            <v-row class="tw-flex-col sm:tw-flex-row tw-justify-center tw-gap-y-2 sm:tw-gap-x-4 tw-p-4">
                <v-col
                    cols="12"
                    class="tw-bg-gray-lighter-lighter tw-rounded-lg tw-h-fit tw-mb-[-0.75rem] tw-mt-[-1.74rem]">
                    <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-xl tw-mb-4">{{ $t('reservedArea.investmentDetails.documents') }}</p>
                    <!-- if the fund does not have any documents -->
                    <div
                        class="tw-flex tw-flex-row tw-items-center tw-justify-between tw-bg-white tw-rounded-lg tw-mb-2.5 tw-p-1"
                        v-if="publicDocs.length == 0">
                        <div class="tw-flex tw-gap-x-2">
                            <v-icon>
                                <v-img
                                    height="20"
                                    src="/images/ReservedArea/icons/google-docs.svg"></v-img>
                            </v-icon>
                            <p class="tw-text-secondary tw-text-left tw-text-base">N/A</p>
                        </div>
                        <div>
                            <v-icon>
                                <v-img
                                    height="16"
                                    src="/images/ReservedArea/icons/download2.svg"></v-img>
                            </v-icon>
                        </div>
                    </div>
                    <!-- if the fund has documents -->
                    <div
                        class="tw-flex tw-flex-row tw-items-center tw-justify-between tw-bg-white tw-rounded-lg tw-mb-2.5 tw-p-1"
                        v-else
                        v-for="document in publicDocs"
                        :key="document">
                        <div class="tw-flex tw-gap-x-2">
                            <v-icon>
                                <v-img
                                    height="20"
                                    src="/images/ReservedArea/icons/google-docs.svg"></v-img>
                            </v-icon>
                            <p class="tw-text-secondary tw-text-left tw-text-base">{{ document.fileName ? document.fileName : 'N/A' }}</p>
                        </div>
                        <div>
                            <v-btn
                                elevation="0"
                                :loading="isDownloadBtnLoading[document.id] || false"
                                @click="downloadFile(document.id, document.fileName)">
                                <v-icon class="tw-text-primary">
                                    <v-img
                                        height="16"
                                        src="/images/ReservedArea/icons/download2.svg"></v-img>
                                </v-icon>
                            </v-btn>
                            <!-- <v-icon @click="downloadFile(document.id, document.fileName)">
                                <v-img
                                    height="16"
                                    src="/images/ReservedArea/icons/download2.svg"></v-img>
                            </v-icon> -->
                        </div>
                    </div>
                </v-col>
            </v-row>
        </div>
    </v-container>
    <div
        v-if="isLoading"
        class="tw-h-full tw-flex tw-items-center tw-justify-center">
        <v-progress-circular
            indeterminate
            color="primary"
            model-value="20"></v-progress-circular>
    </div>
</template>

<script setup lang="ts">
    import { ref, watch, computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { useRouter } from 'vue-router';
    import { getFundDetail, getCustomerFunds, getDocument } from '@/api/dbn';
    import { useAuthStore } from '@/store/modules/auth';
    import { useLanguageStore } from '@/store/modules/language';
    import { useRoute } from 'vue-router';
    import { me } from '@/api/user';
    import { get } from 'lodash';
    import moment from 'moment';

    const { t } = useI18n();
    const $route = useRoute();
    const $router = useRouter();
    //language store
    const languageStore = useLanguageStore();
    const language = computed(() => languageStore.getLanguage);

    //store token
    const authStore = useAuthStore();
    const token = authStore.getToken;
    const user = authStore.getUser;

    let fundId = $route.params.id;
    const panel = ref(0);
    const fund = ref([]);
    const publicDocs = ref([]);
    const base64 = ref('');
    const isLoading = ref(true);
    const isDownloadBtnLoading = ref({});

    //breadcrumbs
    const breadCrumbItems = ref([
        {
            title: t('reservedArea.breadcrumbs.investments'),
            disabled: false,
            active: $router.currentRoute.value.name == 'Investment' ? true : false,
            href: '/reserved-area/investment',
        },
        {
            disabled: false,
            active: $router.currentRoute.value.name == 'InvestmentDetails' ? true : false,
        },
    ]);

    //fetch fund details
    const fetchFundDetails = () =>
        getFundDetail(language.value, fundId, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((response) => {
                fund.value = [response.data];
                if (user?.dbnCode || user?.dbns[0]?.code) fetchCustomerFunds();
                else isLoading.value = false;
            })
            .catch((error) => {
                console.log(error);
            });
    //fetch customer funds
    const fetchCustomerFunds = () =>
        getCustomerFunds(language.value, user?.dbnCode ? user?.dbnCode : user?.dbns[0]?.code, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((response) => {
                if (response.data.filter((customerFund: any) => customerFund.id == fundId).length == 0) {
                    //if the fund is not in the customer's funds, then it will appear only the fund's name, description, etc, because the customer doesn't have that fund in his portfolio
                    isLoading.value = false;
                    return;
                }
                //if the fund is in the customer's funds, then it will appear extra information (e.g. category, value, quantity, distributedValue, etc)
                //if not, it won't appear extra information, only the fund's name, description, etc, because the customer doesn't have that fund in his portfolio
                fund.value = response.data.filter((customerFund: any) => customerFund.id == fundId);
                fund.value.forEach((f: any) => {
                    publicDocs.value = [...new Set([...f.publicDocuments, ...publicDocs.value])];
                });
                isLoading.value = false;
            })
            .catch((error) => {
                console.log(error);
                isLoading.value = false;
            });
    fetchFundDetails();
    //download file
    const downloadFile = async (documentId: any, name: any) => {
        try {
            isDownloadBtnLoading.value[documentId] = true;

            await fetchFundDocument(documentId);

            // Convert base64 to blob for better browser compatibility (especially Safari)
            const byteCharacters = atob(base64.value);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });

            // Create blob URL instead of data URL for better Safari support
            const blobUrl = URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = blobUrl;
            downloadLink.download = name;
            downloadLink.style.display = 'none';

            // Append to DOM, click, and cleanup
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);

            // Clean up the blob URL to prevent memory leaks
            setTimeout(() => {
                URL.revokeObjectURL(blobUrl);
            }, 100);
        } catch (error) {
            console.log(error);
        } finally {
            isDownloadBtnLoading.value[documentId] = false;
        }
    };
    //fetch fund document
    const fetchFundDocument = (documentId: any) =>
        getDocument(documentId, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((response) => {
                base64.value = response.data.base64;
            })
            .catch((error) => {
                console.log(error);
            });
    //watch fund
    watch(fund, () => {
        if (fund.value[0].name) {
            breadCrumbItems.value[1].title = fund.value[0].name;
            breadCrumbItems.value[1].href = `/reserved-area/investment/${fund.value[0].id}`;
        }
    });
    //methods to get values (upValue, acquisitionValueInitial, acquisitionValueCurrent, currentValuation, distributedDividends, returnedCapital)
    // getUpValue (4 decimal places)
    const getUpValue = (investments: any) => {
        if (!investments) return 'N/A';
        const sum = investments.reduce((sum, investment) => {
            if (investment.pricePerUnit == null || investment.pricePerUnit == 0) return sum;
            return sum + investment.pricePerUnit;
        }, 0);
        return formatCurrency4Decimals(sum);
    };

    // getAcquisitionValueInitial (2 decimal places)
    const getAcquisitionValueInitial = (investments: any) => {
        if (!investments) return 'N/A';
        const sum = investments.reduce((sum, investment) => {
            if (investment.initialValue == null || investment.initialValue == 0) return sum;
            return sum + investment.initialValue;
        }, 0);
        return formatCurrency2Decimals(sum);
    };

    // getAcquisitionValueCurrent (2 decimal places)
    // For PPR, Discovery, and Innovation funds, use currentInvestment field directly
    const getAcquisitionValueCurrent = (investments: any, fundName: string = '') => {
        if (!investments) return 'N/A';

        // Check if fund is PPR, Discovery, Impact, Viriatus, Next Tech, Northern, or Innovation (case-insensitive)
        const isPPROrDiscoveryOrInnovation =
            fundName &&
            (isSpecialFund(fundName) || isSelectOrShortTermFund(fundName) || fundName.toLowerCase().includes('innovation'));

        if (isPPROrDiscoveryOrInnovation) {
            // Use currentInvestment field for PPR/Discovery/Innovation funds
            const sum = investments.reduce((sum, investment) => {
                if (investment.currentInvestment == null || investment.currentInvestment == 0) return sum;
                return sum + investment.currentInvestment;
            }, 0);
            return formatCurrency2Decimals(sum);
        } else {
            // Use initialValue - returnedCapital for other funds
            const sumInitial = investments.reduce((sum, investment) => {
                if (investment.initialValue == null || investment.initialValue == 0) return sum;
                return sum + investment.initialValue;
            }, 0);
            const sumReturnedCapital = investments.reduce((sum, investment) => {
                if (investment.nextDistributionValue == null || investment.nextDistributionValue == 0) return sum;
                return sum + investment.nextDistributionValue;
            }, 0);
            const sumCurrent = sumInitial - sumReturnedCapital;
            return formatCurrency2Decimals(sumCurrent);
        }
    };

    // getCurrentValuation (2 decimal places)
    const getCurrentValuation = (investments: any) => {
        if (!investments) return 'N/A';
        const sum = investments.reduce((sum, investment) => {
            if (investment.value == null || investment.value == 0) return sum;
            return sum + investment.value;
        }, 0);
        return formatCurrency2Decimals(sum);
    };

    // getDistributedDividends (2 decimal places)
    // Returns '-' for PPR, Discovery, and Innovation funds
    const getDistributedDividends = (investments: any, fundName: string = '') => {
        if (!investments) return 'N/A';

        // Check if fund is PPR, Discovery, Impact, Viriatus, Next Tech, Northern, or Innovation (case-insensitive)
        const isPPROrDiscoveryOrInnovation = fundName && (isSpecialFund(fundName) || fundName.toLowerCase().includes('innovation'));

        if (isPPROrDiscoveryOrInnovation) {
            return '-';
        }

        const sum = investments.reduce((sum, investment) => {
            if (investment.distributedValue == null || investment.distributedValue == 0) return sum;
            return sum + investment.distributedValue;
        }, 0);
        return formatCurrency2Decimals(sum);
    };

    // getReturnedCapital (2 decimal places)
    // For Innovation funds, uses returnedValue field
    const getReturnedCapital = (investments: any, fundName: string = '') => {
        if (!investments) return 'N/A';

        // Check if fund is Innovation (case-insensitive)
        const isInnovation = fundName && fundName.toLowerCase().includes('innovation');

        if (isInnovation) {
            // Use returnedValue field for Innovation funds
            const sum = investments.reduce((sum, investment) => {
                if (investment.returnedValue == null || investment.returnedValue == 0) return sum;
                return sum + investment.returnedValue;
            }, 0);
            return formatCurrency2Decimals(sum);
        }

        // For other funds, use nextDistributionValue
        const sum = investments.reduce((sum, investment) => {
            if (investment.nextDistributionValue == null || investment.nextDistributionValue == 0) return sum;
            return sum + investment.nextDistributionValue;
        }, 0);
        return formatCurrency2Decimals(sum);
    };

    // getParticipationUnits
    const getParticipationUnits = (investments: any) => {
        if (!investments) return 'N/A';
        const sum = investments.reduce((sum, investment) => {
            if (investment.quantity == null || investment.quantity == 0) return sum;
            return sum + investment.quantity;
        }, 0);

        // Check if number has decimals
        if (sum.toString().includes('.')) {
            const sumString = sum.toString();
            const parts = sumString.split('.');
            const integerPart = parts[0];
            const decimalPart = parts[1] || '';

            // Add thousands separator (dot) to integer part
            const integerWithSeparator = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

            // Return with original decimal places (no rounding or padding)
            return decimalPart ? `${integerWithSeparator},${decimalPart}` : integerWithSeparator;
        } else {
            // Add thousands separator (dot) for whole numbers
            return sum.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        }
    };

    const formatCurrency2Decimals = (value: number) => {
        return value.toLocaleString('de-DE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const formatCurrency4Decimals = (value: number) => {
        return value.toLocaleString('de-DE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
        });
    };

    //format date from YYYY-MM-DDTHH:mm:ss to DD/MM/YYYY
    const formatDate = (date) => {
        return moment(date).format('DD/MM/YYYY');
    };

    // Helper function to check if fund name includes PPR, Discovery, Impact, Viriatus, Next Tech, Northern, or Global Listed Property
    const isSpecialFund = (fundName: string) => {
        if (!fundName) return false;
        const lowerName = fundName.toLowerCase();
        return (
            lowerName.includes('ppr') ||
            lowerName.includes('discovery') ||
            lowerName.includes('impact') ||
            lowerName.includes('viriatus') ||
            lowerName.includes('next tech') ||
            lowerName.includes('northern') ||
            lowerName.includes('global listed property')
        );
    };

    const isSelectOrShortTermFund = (fundName: string) => {
        if (!fundName) return false;
        const lowerName = fundName.toLowerCase();
        return lowerName.includes('select') || lowerName.includes('short term');
    };

    // Check if the fund name includes "PPR", "Discovery", "Impact", "Viriatus", "Next Tech", "Northern", or "Global Listed Property" (case-insensitive)
    const shouldHideDividendsAndCapital = computed(() => {
        if (!fund.value || fund.value.length === 0 || !fund.value[0].name) {
            return false;
        }
        return isSpecialFund(fund.value[0].name || '');
    });

    // Check if the fund name includes "Innovation" (case-insensitive)
    const shouldHideDistributedDividends = computed(() => {
        if (!fund.value || fund.value.length === 0 || !fund.value[0].name) {
            return false;
        }
        const fundName = (fund.value[0].name || '').toLowerCase();
        return fundName.includes('innovation');
    });

    const shouldShowSelectShortTermCards = computed(() => {
        if (!fund.value || fund.value.length === 0 || !fund.value[0].name) {
            return false;
        }
        return isSelectOrShortTermFund(fund.value[0].name || '');
    });

    // Get visible card indices
    // Hide cards 3 and 4 if fund is PPR or Discovery or Global Listed Property
    // Hide only card 4 if fund is Innovation
    const visibleCardIndices = computed(() => {
        const allIndices = [1, 2, 3, 4];
        if (shouldShowSelectShortTermCards.value) {
            // Only current valuation (participation units is shown as the second fixed card)
            return allIndices.filter((i) => i === 1);
        }
        if (shouldHideDividendsAndCapital.value) {
            // Hide cards 3 and 4 for PPR/Discovery/Global Listed Property
            return allIndices.filter((i) => i !== 3 && i !== 4);
        }
        if (shouldHideDistributedDividends.value) {
            // Hide only card 4 for Innovation
            return allIndices.filter((i) => i !== 4);
        }
        return allIndices;
    });
</script>

<style scoped></style>
<style>
    .mdi-chevron-down {
        color: #131d3c !important;
    }
    .mdi-chevron-up {
        color: #131d3c !important;
    }
    .no-opacity .v-expansion-panel-title__overlay {
        opacity: 0 !important;
    }
</style>
